// ─── VENDORED — DO NOT EDIT ─────────────────────────────────────────────────
// CONTRACT:I4-POLICY-DECISION.1.0 — vendored decision/marking substrate (see README.md)
// Source: willackerly/TDFLite tag v0.10.0 (commit 2a1ec7cb639a7a77b8ec9e14df4cb0b139cb43aa)
// Path:   js/packages/classification-schema/src/evaluate.ts
// Sync:   scripts/sync-classification-schema.sh (re-vendors on a pin bump)
// Drift:  scripts/check-classification-schema-drift.sh (this header + the
//         "@tdflite/contracts" specifier rewrite + the de-triggered upstream
//         contract line are NORMALIZED; any OTHER divergence FAILS the gate).
// ─── END VENDORED HEADER ─────────────────────────────────────────────────────
// upstream-contract F1-CLASSIFICATION-SCHEMA.0.1 (TDFLite; see vendored header)
/** The read-down ABAC evaluator — the classification DECISION primitive.
 *
 *  Single-sourced from tak-tdf abac-pdp/src/evaluate.ts (evaluateAbac + evaluateGroup)
 *  and its faithful Go port go-tak-server/internal/decision/pdp_abac.go, so a decision
 *  made here matches the KAS / gateway BY CONSTRUCTION. Deterministic, case-insensitive,
 *  no I/O.
 *
 *  Per attribute:
 *    hierarchy → permit iff the subject's value index <= the lowest (highest-level) bound
 *                index. Index 0 = highest (TOP_SECRET). READ-DOWN: a SECRET subject reads
 *                CONFIDENTIAL; is denied TOP_SECRET. `userValues[0]` is LOAD-BEARING and
 *                is NEVER sorted — the subject holds exactly one hierarchy value.
 *    allOf     → the subject must hold EVERY bound value.
 *    anyOf     → the subject must hold AT LEAST ONE bound value.
 *  Across attributes: permit iff EVERY bound attribute is satisfied (AND).
 *  Fail-closed EVERYWHERE: an unparseable FQN, an unknown attribute, a value-not-in-set,
 *  or no held value ⇒ deny. */

import { CLASSIFICATION_HIERARCHY } from "./lattice";

/** The three OpenTDF attribute rules. */
export type RuleType = "hierarchy" | "allOf" | "anyOf";

/** A policy attribute definition. For `hierarchy`, `values` is ORDERED — index 0 is the
 *  highest level (e.g. TOP_SECRET). */
export interface AttributeDef {
  name: string;
  rule: RuleType;
  values: string[];
}

/** The policy bundle — the closed attribute vocabulary a decision is made against. */
export interface PolicyBundle {
  namespace: string;
  attributes: AttributeDef[];
}

/** A subject's held attribute values, keyed by attribute name. A value may be a scalar
 *  (hierarchy) or a list (allOf / anyOf). Mirrors the JWT claims shape. */
export type SubjectClaims = Record<string, string | string[]>;

/** Per-attribute outcome — carries the human reason for an inspector / audit log. */
export interface AttributeVerdict {
  attr: string;
  rule: RuleType | "?";
  satisfied: boolean;
  reason: string;
  dataValues: string[];
  userValues: string[];
}

/** The decision over an object's bound attribute FQNs. */
export interface Decision {
  /** Permit iff EVERY bound attribute is satisfied (AND across attributes). */
  permit: boolean;
  /** One-line human summary (for the inspector / logs). */
  reason: string;
  /** Per-attribute detail. */
  verdicts: AttributeVerdict[];
}

const lc = (s: string): string => String(s).toLowerCase();

interface ParsedFqn {
  namespace: string;
  name: string;
  value: string;
}

/** Parse `https://ns/attr/<name>/value/<value>` → parts, or null if malformed. A plain
 *  string split (NOT `new URL()`) so the primitive stays bundler/worker-safe with zero
 *  DOM coupling.
 *
 *  FAIL-CLOSED and byte-exact with the Go twin (pkg/pdp/classification.ParseAttrFqn) and
 *  the KAS-side authority (go-tak-server pdp_abac.go parseAttrFqn): the scheme must be a
 *  literal `https://` (any other scheme, or a schemeless FQN, is unparseable), the path is
 *  a plain split, and the value is NOT percent-decoded (the bound vocabulary is bare
 *  alphanumerics). This is a DELIBERATE strictening away from the permissive canonical
 *  tak-tdf abac-pdp fqn.ts (which strips ANY scheme and `decodeURIComponent`s the value):
 *  an EDGE filter must never out-permit the KAS that re-checks the wrap, so on a
 *  non-canonical FQN it fails closed to the SAME deny the authority makes — the twins agree
 *  by construction. Pinned by the staged abac_decide non-https + percent-encoded vectors. */
function parseAttrFqn(fqn: string): ParsedFqn | null {
  const scheme = "https://";
  if (!fqn.startsWith(scheme)) return null; // https-only, fail-closed.
  const rest = fqn.slice(scheme.length);
  const slash = rest.indexOf("/");
  if (slash < 0) return null;
  const namespace = rest.slice(0, slash);
  const parts = rest.slice(slash + 1).split("/"); // plain split (no filter) — matches the Go twin.
  // Expected: ["attr", "<name>", "value", "<value>"].
  if (parts.length >= 4 && parts[0] === "attr" && parts[2] === "value") {
    const name = parts[1];
    const value = parts[3];
    if (name === undefined || value === undefined) return null;
    return { namespace, name, value }; // no percent-decoding — the twin decodes nothing.
  }
  return null;
}

/** Build the FQN for a namespace/attribute/value. The inverse of the internal parse;
 *  exported so vectors + call sites bind values to the bundle the SAME way the evaluator
 *  reads them. Bare concatenation — NO percent-encoding — to stay byte-exact with the Go
 *  twin (BuildAttrFqn) and symmetric with the no-decode `parseAttrFqn` above; the bound
 *  vocabulary is bare alphanumerics, for which encoding is a no-op. */
export function buildAttrFqn(namespace: string, name: string, value: string): string {
  return `https://${namespace}/attr/${name}/value/${value}`;
}

function userValuesFor(claims: SubjectClaims, attr: string): string[] {
  const claim = claims[attr];
  if (Array.isArray(claim)) return claim.map(lc);
  if (claim != null && claim !== "") return [lc(String(claim))];
  return [];
}

/** Evaluate ONE attribute group: the bound data values for a single attribute against
 *  the subject's held values for it. Exported for granular use + born-with-vectors proof. */
export function evaluateGroup(
  def: AttributeDef,
  dataValues: string[],
  userValues: string[],
): AttributeVerdict {
  const base = {
    attr: def.name,
    rule: def.rule,
    dataValues,
    userValues,
  } as const;

  switch (def.rule) {
    case "hierarchy": {
      const definedIdx = (v: string): number =>
        def.values.findIndex((x) => lc(x) === lc(v));
      // userValues[0] is LOAD-BEARING and is NEVER sorted — the subject's single
      // hierarchy value, in claim order. Empty ⇒ deny (fail-closed).
      const held = userValues.length > 0 ? userValues[0] : undefined;
      const userIdx = held === undefined ? -1 : definedIdx(held);
      // The lowest data index = the HIGHEST level among the bound values.
      let minDataIdx = Infinity;
      for (const v of dataValues) {
        const i = definedIdx(v);
        if (i >= 0 && i < minDataIdx) minDataIdx = i;
      }
      if (userIdx < 0 || minDataIdx === Infinity) {
        return { ...base, satisfied: false, reason: "value not in attribute set" };
      }
      const ok = userIdx <= minDataIdx; // read-down
      const heldName = (held as string).toUpperCase();
      const boundName = (def.values[minDataIdx] as string).toUpperCase();
      return {
        ...base,
        satisfied: ok,
        reason: ok ? `holds ${heldName} >= ${boundName}` : `holds ${heldName} < ${boundName}`,
      };
    }
    case "allOf": {
      const missing = dataValues.filter((v) => !userValues.includes(lc(v)));
      const ok = missing.length === 0;
      return {
        ...base,
        satisfied: ok,
        reason: ok
          ? `holds all ${dataValues.length} required`
          : `lacks ${missing.map((v) => v.toUpperCase()).join(", ")}`,
      };
    }
    case "anyOf": {
      const matches = dataValues.filter((v) => userValues.includes(lc(v)));
      const ok = matches.length > 0;
      return {
        ...base,
        satisfied: ok,
        reason: ok
          ? `holds ${matches.map((v) => v.toUpperCase()).join(", ")}`
          : `holds none of ${dataValues.map((v) => v.toUpperCase()).join(", ")}`,
      };
    }
    default:
      return { ...base, satisfied: false, reason: `unknown rule "${def.rule}"` };
  }
}

/** Decide whether a subject may SEE an object, given the object's bound attribute FQNs,
 *  the policy bundle, and the subject's held claims. */
export function evaluateAbac(
  boundFqns: string[],
  bundle: PolicyBundle,
  claims: SubjectClaims,
): Decision {
  // Group bound FQNs by attribute name; collect (lowercased) data values.
  const groups = new Map<string, string[]>();
  const order: string[] = [];
  for (const fqn of boundFqns) {
    const parsed = parseAttrFqn(fqn);
    if (!parsed) {
      // Fail-closed: an unparseable binding denies the whole object.
      return {
        permit: false,
        reason: `unparseable bound attribute: ${fqn}`,
        verdicts: [
          {
            attr: fqn,
            rule: "?",
            satisfied: false,
            reason: "unparseable FQN",
            dataValues: [],
            userValues: [],
          },
        ],
      };
    }
    if (!groups.has(parsed.name)) {
      groups.set(parsed.name, []);
      order.push(parsed.name);
    }
    groups.get(parsed.name)!.push(lc(parsed.value));
  }

  // No bound attributes ⇒ unbound object ⇒ visible to all.
  if (order.length === 0) {
    return { permit: true, reason: "no bound attributes", verdicts: [] };
  }

  const verdicts: AttributeVerdict[] = order.map((name) => {
    const def = bundle.attributes.find((a) => lc(a.name) === lc(name));
    const dataValues = groups.get(name)!;
    if (!def) {
      // Fail-closed: an unknown attribute denies.
      return {
        attr: name,
        rule: "?",
        satisfied: false,
        reason: "unknown attribute",
        dataValues,
        userValues: [],
      };
    }
    return evaluateGroup(def, dataValues, userValuesFor(claims, def.name));
  });

  const permit = verdicts.every((v) => v.satisfied);
  const fail = verdicts.find((v) => !v.satisfied);
  return {
    permit,
    reason: permit
      ? "permit — " + verdicts.map((v) => v.reason).join("; ")
      : `deny — ${fail!.attr}: ${fail!.reason}`,
    verdicts,
  };
}

/** The canonical defense.local policy bundle — the SAME vocabulary the KAS / gateway
 *  evaluate, so a staged vector can reference it and a decision made here matches
 *  upstream BY CONSTRUCTION. Transcribed verbatim from tak-tdf abac-pdp/src/policy.ts
 *  SEED_POLICY_BUNDLE (:49); the classification ladder is single-sourced from lattice.ts
 *  (CLASSIFICATION_HIERARCHY — index 0 = TOP_SECRET, read-down). The per-attribute
 *  vocabularies are disjoint, so a marking value binds to exactly one attribute. */
export const SEED_BUNDLE: PolicyBundle = {
  namespace: "defense.local",
  attributes: [
    // HIERARCHY — index 0 = TOP_SECRET (highest); read-down.
    { name: "classification", rule: "hierarchy", values: [...CLASSIFICATION_HIERARCHY] },
    // ALL_OF — the subject must hold every bound value. SCI split into control-system + subcompartment.
    { name: "sci_control_system", rule: "allOf", values: ["SI", "TK", "HCS"] },
    { name: "sci_compartment", rule: "allOf", values: ["SI-G", "TK-RUFF", "SI-GAMMA", "ECI"] },
    { name: "sap_program", rule: "allOf", values: ["ALPHA", "BRAVO", "CHARLIE", "WASP", "COBALT", "RAVEN", "TALON"] },
    // Unified dissemination / handling caveats. NOFORN merged here; REL_TO dropped.
    { name: "handling_caveat", rule: "allOf", values: ["NOFORN", "ORCON", "PROPIN", "IMCON", "RELIDO", "FOUO", "LIMDIS"] },
    // ANY_OF — the subject must hold at least one bound value. FVEY/NATO + NATO members +
    // CENTCOM-AOR partner trigraphs are DIRECT releasability values.
    {
      name: "releasability",
      rule: "anyOf",
      values: [
        "USA", "GBR", "CAN", "AUS", "NZL", "FVEY", "NATO",
        "ALB", "BEL", "BGR", "HRV", "CZE", "DNK", "EST", "FIN", "FRA", "DEU", "GRC", "HUN",
        "ISL", "ITA", "LVA", "LTU", "LUX", "MNE", "NLD", "MKD", "NOR", "POL", "PRT", "ROU",
        "SVK", "SVN", "ESP", "SWE", "TUR",
        "AFG", "BHR", "EGY", "IRQ", "JOR", "KAZ", "KWT", "KGZ", "LBN", "OMN", "PAK", "QAT",
        "SAU", "TJK", "TKM", "ARE", "UZB", "YEM",
      ],
    },
    // Fuller formal CAPCO attrs (allOf).
    { name: "accm_compartment", rule: "allOf", values: ["ACCM-SANDSTORM", "ACCM-IRONCLAD"] },
    { name: "fgi_constraint", rule: "allOf", values: ["FGI-GBR", "FGI-DEU", "FGI-AUS", "FGI-NATO"] },
    { name: "atomic_energy", rule: "allOf", values: ["RD", "FRD", "CNWDI", "DCNI"] },
    { name: "cui_category", rule: "allOf", values: ["SP-CTI", "SP-PRVCY", "SP-LEI"] },
  ],
};
